import { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../../lib/apiBase.js';
import { Button, Modal } from '../ds/index.js';
import { apiHeaders, readFileAsDataUrl, photoErrorMessage, PHOTO_MAX_BYTES, loadMembers } from '../people/membersStore.js';

/**
 * Import de photos de profil en lot (tasks/profils-messages-stats-plan.md) :
 * des fichiers nommés par l'e-mail du membre (`ines@copyhouse.fr.jpg`), ou par
 * le début de l'e-mail s'il n'y a pas d'ambiguïté (`ines.jpg`). Aperçu de
 * l'association avant l'envoi ; une photo en échec n'arrête pas les autres.
 */

interface MemberRef { id: string; displayName: string; email: string }

type Row = { file: File; member: MemberRef | null; status: 'ready' | 'sending' | 'done' | 'error'; message?: string };

function baseName(name: string): string {
  return name.replace(/\.[a-z0-9]+$/i, '').trim().toLowerCase();
}

export function matchMember(fileName: string, members: MemberRef[]): MemberRef | null {
  const base = baseName(fileName);
  const exact = members.find((m) => m.email.toLowerCase() === base);
  if (exact) return exact;
  const byLocal = members.filter((m) => m.email.toLowerCase().split('@')[0] === base);
  return byLocal.length === 1 ? byLocal[0]! : null;
}

export function PhotoImportModal({ members, onClose, onDone }: {
  members: MemberRef[];
  onClose: () => void;
  onDone: (sent: number) => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);

  const pick = (files: FileList | null) => {
    if (!files) return;
    setFinished(false);
    setRows([...files].filter((f) => f.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif|gif|avif)$/i.test(f.name))
      .map((file) => {
        const member = matchMember(file.name, members);
        if (file.size > PHOTO_MAX_BYTES) return { file, member, status: 'error' as const, message: 'Plus de 8 Mo' };
        return { file, member, status: 'ready' as const };
      }));
  };

  const ready = useMemo(() => rows.filter((r) => r.member && r.status === 'ready'), [rows]);
  // Deux fichiers pour le même membre : le dernier l'emporterait, on le signale.
  const duplicates = useMemo(() => {
    const seen = new Map<string, number>();
    for (const r of ready) seen.set(r.member!.id, (seen.get(r.member!.id) ?? 0) + 1);
    return new Set([...seen].filter(([, n]) => n > 1).map(([id]) => id));
  }, [ready]);

  const send = async () => {
    setRunning(true);
    let sent = 0;
    for (const row of ready) {
      setRows((prev) => prev.map((r) => (r === row ? { ...r, status: 'sending' } : r)));
      let next: Row;
      try {
        const image = await readFileAsDataUrl(row.file);
        const res = await fetch(`${API_BASE}/admin/members/${encodeURIComponent(row.member!.id)}/photo`, {
          method: 'PUT', headers: apiHeaders(true), body: JSON.stringify({ image }),
        });
        if (res.ok) { sent++; next = { ...row, status: 'done' }; }
        else next = { ...row, status: 'error', message: photoErrorMessage((await res.json().catch(() => ({})) as { error?: string }).error, res.status) };
      } catch {
        next = { ...row, status: 'error', message: 'Réseau' };
      }
      setRows((prev) => prev.map((r) => (r.file === row.file ? next : r)));
    }
    await loadMembers(0);
    setRunning(false);
    setFinished(true);
    onDone(sent);
  };

  useEffect(() => () => { /* rien à libérer : pas d'URL objet */ }, []);

  const unmatched = rows.filter((r) => !r.member).length;

  return (
    <Modal
      title="Importer des photos de profil"
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{finished ? 'Fermer' : 'Annuler'}</Button>
          {!finished && (
            <Button variant="primary" disabled={running || ready.length === 0} onClick={() => { void send(); }}>
              {running ? 'Envoi…' : `Envoyer ${ready.length} photo${ready.length > 1 ? 's' : ''}`}
            </Button>
          )}
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p className="pl-help" style={{ margin: 0 }}>
          Nomme chaque fichier avec l&apos;e-mail du membre (par exemple <strong>ines@copyhouse.fr.jpg</strong>), ou avec le début de
          l&apos;e-mail (<strong>ines.jpg</strong>). Les photos sont recadrées au carré, centrées sur le visage. Chaque membre peut
          ensuite changer la sienne.
        </p>
        <label className="pl-btn pl-btn-secondary pl-btn-md" style={{ alignSelf: 'flex-start', cursor: 'pointer' }}>
          Choisir les photos
          <input type="file" accept="image/*" multiple hidden onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
        </label>
        {rows.length > 0 && (
          <>
            <p className="pl-help" style={{ margin: 0 }}>
              {ready.length} photo{ready.length > 1 ? 's' : ''} associée{ready.length > 1 ? 's' : ''} à un membre
              {unmatched > 0 && `, ${unmatched} sans membre correspondant (ignorée${unmatched > 1 ? 's' : ''})`}.
            </p>
            <div className="pl-panel" style={{ overflowX: 'auto' }}>
              <table className="pl-table">
                <thead><tr><th>Fichier</th><th>Membre</th><th>État</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.file.name + r.file.size}>
                      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.file.name}</td>
                      <td>{r.member ? `${r.member.displayName} (${r.member.email})` : <span className="pl-muted">Aucun membre</span>}
                        {r.member && duplicates.has(r.member.id) && <span className="pl-muted"> · plusieurs fichiers</span>}</td>
                      <td className="pl-muted">
                        {!r.member ? 'Ignorée' : r.status === 'ready' ? 'Prête' : r.status === 'sending' ? 'Envoi…' : r.status === 'done' ? 'Enregistrée' : `Échec : ${r.message ?? ''}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
